import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { apiClient } from '@/lib/api';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { User, Crown, ExternalLink, Swords } from 'lucide-react';
import { ChessUser } from '@/types';

export const Members: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBy, setFilterBy] = useState<'all' | 'with-chess' | 'without-chess'>('all');

  const { data, isLoading, error } = useQuery(
    'allMembers',
    () => apiClient.getAllMembers(),
    {
      refetchInterval: 300000, // Refetch every 5 minutes
    }
  );

  const members = data?.data?.members || [];

  // Filter members based on search term and filter
  const filteredMembers = members.filter((member: ChessUser) => {
    const matchesSearch = 
      member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.chessUsername?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesFilter = 
      filterBy === 'all' ||
      (filterBy === 'with-chess' && member.chessUsername) ||
      (filterBy === 'without-chess' && !member.chessUsername);

    return matchesSearch && matchesFilter;
  });

  const membersWithChess = members.filter((m: ChessUser) => m.chessUsername).length;
  const membersWithoutChess = members.filter((m: ChessUser) => !m.chessUsername).length;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold text-gray-900">Members</h1>
        <div className="grid gap-4">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="animate-pulse bg-white p-6 rounded-lg shadow-sm">
              <div className="flex items-center space-x-4">
                <div className="bg-gray-200 h-12 w-12 rounded-full"></div>
                <div className="flex-1 space-y-2">
                  <div className="bg-gray-200 h-4 w-32 rounded"></div>
                  <div className="bg-gray-200 h-3 w-24 rounded"></div>
                </div>
                <div className="bg-gray-200 h-8 w-16 rounded"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Error loading members</h2>
        <p className="text-gray-600 mb-4">Failed to load member data.</p>
        <Button onClick={() => window.location.reload()}>Try Again</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Members</h1>
          <p className="text-gray-600 mt-1">
            {members.length} total members • {membersWithChess} with Chess.com • {membersWithoutChess} without Chess.com
          </p>
        </div>
      </div>

      {/* Search and Filter */}
      <Card>
        <CardHeader>
          <CardTitle>Search & Filter</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Input
                placeholder="Search by name, email, or Chess.com username..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full"
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant={filterBy === 'all' ? 'primary' : 'outline'}
                size="sm"
                onClick={() => setFilterBy('all')}
              >
                All ({members.length})
              </Button>
              <Button
                variant={filterBy === 'with-chess' ? 'primary' : 'outline'}
                size="sm"
                onClick={() => setFilterBy('with-chess')}
              >
                With Chess.com ({membersWithChess})
              </Button>
              <Button
                variant={filterBy === 'without-chess' ? 'primary' : 'outline'}
                size="sm"
                onClick={() => setFilterBy('without-chess')}
              >
                Without Chess.com ({membersWithoutChess})
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Members List */}
      <Card>
        <CardHeader>
          <CardTitle>All Members ({filteredMembers.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {filteredMembers.length === 0 ? (
            <div className="text-center py-8">
              <User className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No members found</h3>
              <p className="text-gray-600">Try adjusting your search or filter criteria.</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {filteredMembers.map((member: ChessUser) => (
                <div
                  key={member._id}
                  className="flex items-center space-x-4 p-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                >
                  {/* Avatar */}
                  <div className="flex-shrink-0">
                    {member.avatar ? (
                      <img
                        src={member.avatar}
                        alt={member.name}
                        className="h-12 w-12 rounded-full"
                      />
                    ) : (
                      <div className="h-12 w-12 rounded-full bg-gray-200 flex items-center justify-center">
                        <span className="text-sm font-medium text-gray-600">
                          {member.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Member Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-1">
                      <h3 className="text-sm font-medium text-gray-900 truncate">
                        {member.name}
                      </h3>
                      {member.chessUsername ? (
                        <div className="flex items-center space-x-1">
                          <Crown className="h-3 w-3 text-yellow-500" />
                          <span className="text-xs text-gray-500">Chess.com Member</span>
                        </div>
                      ) : (
                        <div className="flex items-center space-x-1">
                          <span className="text-xs text-orange-500 bg-orange-50 px-2 py-1 rounded-full">
                            No Chess.com Profile
                          </span>
                        </div>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 truncate">{member.email}</p>
                    {member.chessUsername ? (
                      <div className="flex items-center space-x-1 mt-1">
                        <span className="text-xs text-gray-500">Chess.com:</span>
                        <a
                          href={`https://www.chess.com/member/${member.chessUsername}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-primary-600 hover:text-primary-700 flex items-center space-x-1"
                        >
                          <span>@{member.chessUsername}</span>
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-1 mt-1">
                        <span className="text-xs text-gray-500">Status:</span>
                        <span className="text-xs text-orange-600">Not connected</span>
                      </div>
                    )}
                  </div>

                  {/* Domain Badge */}
                  <div className="flex-shrink-0">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {member.domain}
                    </span>
                  </div>

                  {/* Challenge Button - Only show for members with Chess.com profiles */}
                  {member.chessUsername && (
                    <div className="flex-shrink-0">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          const challengeUrl = `https://www.chess.com/play/online/new?opponent=${member.chessUsername}`;
                          window.open(challengeUrl, '_blank', 'noopener,noreferrer');
                        }}
                        className="flex items-center space-x-1 text-green-600 border-green-200 hover:bg-green-50 hover:border-green-300"
                      >
                        <Swords className="h-3 w-3" />
                        <span>Challenge</span>
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}; 